import { Routes, Route, Navigate, useLocation, useNavigationType } from 'react-router-dom'
import { useEffect } from 'react'
import * as Sentry from '@sentry/react'
import { AuthProvider } from './components/AuthProvider'
import { CompanyProvider } from './hooks/useCompany'
import { ProtectedRoute } from './components/ProtectedRoute'
import { Layout } from './components/Layout'
import Login from './pages/Login'
import OTPVerification from './pages/OTPVerification'
import AuthCallback from './pages/AuthCallback'
import Onboarding from './pages/Onboarding'
import Dashboard from './pages/Dashboard'
import TimeClock from './pages/TimeClock'
import Timesheets from './pages/Timesheets'
import Schedule from './pages/Schedule'
import Locations from './pages/Locations'
import AuditLogs from './pages/AuditLogs'
import BreakRules from './pages/BreakRules'
import Budgets from './pages/Budgets'
import Preferences from './pages/Preferences'
import Roles from './pages/Roles'
import RoundingRules from './pages/RoundingRules'
import Invitations from './pages/Invitations'
import InvitePage from './pages/InvitePage'
import InvitationLogin from './pages/InvitationLogin'
import InvitationOTPVerification from './pages/InvitationOTPVerification'
import People from './pages/People'
import Teams from './pages/TeamsWithHierarchy'
import Profile from './pages/Profile'
import Signup from './pages/Signup'
import PTO from './pages/PTO'
import TimeSettings from './pages/TimeSettings'
import Availability from './pages/Availability'

// Create Sentry-enhanced routing components
const SentryRoutes = Sentry.withSentryRouting(Routes)

function App() {
  const location = useLocation()
  const navigationType = useNavigationType()
  
  // Track route changes in Sentry
  useEffect(() => {
    Sentry.addBreadcrumb({
      category: 'navigation',
      message: `Navigated to ${location.pathname}`,
      level: 'info',
      data: {
        from: window.history.state?.from || 'unknown',
        to: location.pathname,
        navigationType,
      },
    })
  }, [location, navigationType])
  
  return (
    <AuthProvider>
      <CompanyProvider>
        <SentryRoutes>
        {/* Public routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/auth/verify-otp" element={<OTPVerification />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/invite/:token" element={<InvitePage />} />
        <Route path="/invite/:token/login" element={<InvitationLogin />} />
        <Route path="/invite/:token/verify-otp" element={<InvitationOTPVerification />} />
        
        {/* Onboarding route - protected but doesn't need full layout */}
        <Route path="/onboarding" element={<Onboarding />} />
        
        {/* Protected routes */}
        <Route path="/" element={<Navigate to="/time/clock" replace />} />
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <Layout title="Dashboard">
              <Dashboard />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/time/*" element={
          <ProtectedRoute>
            <Layout title="Time">
              <Routes>
                <Route path="/" element={<Navigate to="/time/clock" replace />} />
                <Route path="/clock" element={<TimeClock />} />
                <Route path="/schedule" element={<Schedule />} />
                <Route path="/timecards" element={<Timesheets />} />
                <Route path="/pto" element={<PTO />} />
                <Route path="/availability" element={<Availability />} />
                <Route path="/settings" element={<TimeSettings />} />
              </Routes>
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/people/*" element={
          <ProtectedRoute requiredPermission="manage_schedules">
            <Layout title="People">
              <Routes>
                <Route path="/" element={<People />} />
                <Route path="/invitations" element={<Invitations />} />
              </Routes>
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/teams" element={
          <ProtectedRoute requiredPermission="manage_schedules">
            <Layout title="Teams">
              <Teams />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/locations" element={
          <ProtectedRoute requiredPermission="manage_locations">
            <Layout title="Locations">
              <Locations />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/reports" element={
          <ProtectedRoute requiredPermission="manage_reports">
            <Layout title="Reports">
              <div className="p-6">
                <h1 className="text-2xl font-semibold text-gray-900">Reports</h1>
                <p className="mt-2 text-gray-600">Generate and view reports.</p>
              </div>
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/settings/*" element={
          <ProtectedRoute requiredRole="ADMIN">
            <Layout title="Settings">
              <div className="p-6">
                <Routes>
                  <Route path="/" element={<Navigate to="/settings/preferences" replace />} />
                  <Route path="/audit-logs" element={<AuditLogs />} />
                  <Route path="/break-rules" element={<BreakRules />} />
                  <Route path="/budgets" element={<Budgets />} />
                  <Route path="/preferences" element={<Preferences />} />
                  <Route path="/roles" element={<Roles />} />
                  <Route path="/rounding-rules" element={<RoundingRules />} />
                </Routes>
              </div>
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/profile" element={
          <ProtectedRoute>
            <Layout title="Profile">
              <Profile />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/help" element={
          <ProtectedRoute>
            <Layout title="Help">
              <div className="p-6">
                <h1 className="text-2xl font-semibold text-gray-900">Help & Support</h1>
                <p className="mt-2 text-gray-600">Find answers to common questions and contact support.</p>
              </div>
            </Layout>
          </ProtectedRoute>
        } />
        </SentryRoutes>
      </CompanyProvider>
    </AuthProvider>
  )
}

export default App